import { useState, useEffect } from "react";
import { Card, Button, Form, ProgressBar, Row, Col } from "react-bootstrap";
import { MdRefresh } from "react-icons/md";
import { FaCopy, FaCheck } from "react-icons/fa";

const generatePassword = (length, options) => {
  let lower = "abcdefghijklmnopqrstuvwxyz";
  let upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let numbers = "0123456789";
  let symbols = "!@#$%^&*()_+[]{}|;:,.<>?";

  if (options.excludeAmbiguous) {
    lower = lower.replace(/[ilo]/g, "");
    upper = upper.replace(/[IO]/g, "");
    numbers = numbers.replace(/[01]/g, "");
    symbols = symbols.replace(/[|`]/g, "");
  }

  let charset = "";
  if (options.lowercase) charset += lower;
  if (options.uppercase) charset += upper;
  if (options.numbers) charset += numbers;
  if (options.symbols) charset += symbols;

  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)];
  }

  if (options.excludeDuplicates) {
    password = [...new Set(password)].join("");
  }

  return password;
};

const calculateStrength = (password, options) => {
  let strength = 0;
  let color = "danger";

  if (options.lowercase) strength += 10;
  if (options.uppercase) strength += 10;
  if (options.numbers) strength += 10;
  if (options.symbols) strength += 20;
  if (password.length >= 8) strength += 20;
  if (password.length >= 12) strength += 30;

  if (strength >= 80) color = "success";
  else if (strength >= 50) color = "warning";
  else if (strength >= 30) color = "info";

  return { strength, color };
};

const PasswordGenerator = ({isDark}) => {
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);
  const [length, setLength] = useState(12);
  const [options, setOptions] = useState({
    lowercase: true,
    uppercase: true,
    numbers: true,
    symbols: true,
    excludeDuplicates: false,
    excludeAmbiguous: false,
  });

  useEffect(() => {
    regeneratePassword();
  }, [length, options]);

  const regeneratePassword = () => {
    setPassword(generatePassword(length, options));
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const { strength, color } = calculateStrength(password, options);

  return (
    <Card className={`pin-card shadow-sm border ${!isDark && "bg-light"}`}>
      <h5 className="text-center fw-bold mb-3">Password Generator</h5>

      <div className="password-box mb-3">
        <Form.Control
          type="text"
          value={password}
          readOnly
          className="password-input interactive-input rounded-3"
          style={{ fontSize: 13, padding: "10px" }}
        />
      </div>

      <div className="mb-3">
        <small className="fw-semibold">Strength</small>
        <ProgressBar now={strength} variant={color} style={{ height: 6 }} />
      </div>

      <Form.Group className="mb-4">
        <Form.Label className="fw-semibold">Password Length</Form.Label>
        <div className="d-flex align-items-center gap-2">
          <Form.Range
            min={4}
            max={64}
            value={length}
            onChange={(e) => setLength(+e.target.value)}
          />
          <Form.Control
            type="number"
            value={length}
            className="interactive-input rounded-1"
            style={{
              width: 65,
              textAlign: "center",
              fontSize: 12,
              padding: "6px 6px",
            }}
            onChange={(e) => setLength(+e.target.value)}
          />
        </div>
      </Form.Group>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Check
            type="switch"
            style={{ fontSize: 13 }}
            label="Lowercase (a-z)"
            checked={options.lowercase}
            onChange={() =>
              setOptions({ ...options, lowercase: !options.lowercase })
            }
          />
          <Form.Check
            type="switch"
            style={{ fontSize: 13 }}
            label="Uppercase (A-Z)"
            checked={options.uppercase}
            onChange={() =>
              setOptions({ ...options, uppercase: !options.uppercase })
            }
          />
          <Form.Check
            type="switch"
            style={{ fontSize: 13 }}
            label="Numbers (0-9)"
            checked={options.numbers}
            onChange={() =>
              setOptions({ ...options, numbers: !options.numbers })
            }
          />
        </Col>

        <Col md={6}>
          <Form.Check
            type="switch"
            style={{ fontSize: 13 }}
            label="Symbols"
            checked={options.symbols}
            onChange={() =>
              setOptions({ ...options, symbols: !options.symbols })
            }
          />
          <Form.Check
            type="switch"
            style={{ fontSize: 13 }}
            label="No Duplicates"
            checked={options.excludeDuplicates}
            onChange={() =>
              setOptions({
                ...options,
                excludeDuplicates: !options.excludeDuplicates,
              })
            }
          />
          <Form.Check
            type="switch"
            style={{ fontSize: 13 }}
            label="No Ambiguous"
            checked={options.excludeAmbiguous}
            onChange={() =>
              setOptions({
                ...options,
                excludeAmbiguous: !options.excludeAmbiguous,
              })
            }
          />
        </Col>
      </Row>

      <div className="d-flex gap-2">
        <Button
          variant="outline-secondary"
          className="w-100 rounded-1 hstack gap-2 justify-content-center"
          onClick={regeneratePassword}
        >
          <MdRefresh size={18} /> Generate
        </Button>

        <Button
          variant={copied ? "success" : "primary"}
          className="w-100 rounded-1 hstack gap-2 justify-content-center"
          onClick={copyPassword}
        >
          {copied ? <FaCheck size={15} /> : <FaCopy size={15} />}{" "}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </Card>
  );
};

export default PasswordGenerator;
